import originalFetch from "unfetch/dist/unfetch.js"
import fetchRetry from "fetch-retry"
var fetch = fetchRetry(originalFetch)

const MAX_RETRY_ATTEMPTS = 5

/**
 * Encapsulates an error response from a call to `fetch()`. Based on the interface of `HTTPError` in the `ky` library.
 * @param {Object} response the response object from `fetch()`
 * @see https://github.com/sindresorhus/ky/blob/2049aff/source/errors/HTTPError.ts
 */
export class HTTPError extends Error {
  constructor(response) {
    const code = response.status || response.status === 0 ? response.status : ""
    const title = response.statusText || ""
    const status = `${code} ${title}`.trim()
    const reason = status ? `status code ${status}` : "an unknown error"

    super(`Request failed with ${reason}`)

    this.name = "HTTPError"
    this.response = response
  }
}

const fetchCSRF = () => {
  return fetch("/csrf-token")
    .then((response) => {
      if (response.ok) return response.json()
      return Promise.reject({ response })
    })
    .then(({ param, token }) => Promise.resolve({ param, token }))
}

const handleError = (response, url, { headers, ...options }, delay, attempt) => {
  if (attempt >= MAX_RETRY_ATTEMPTS) throw new HTTPError(response)

  switch (response.status) {
    // Retry authorization errors after fetching a fresh CSRF token to try
    // and work around potential caching and/or invalid session problems
    //
    // Basically try and handle https://github.com/rails/rails/issues/21948
    // more gracefully.
    case 401: {
      return fetchCSRF()
        .then(({ token }) => {
          headers ??= {}
          headers["X-CSRF-Token"] = token

          return doRetryFetch(url, { headers, ...options }, delay, attempt)
        })
        .catch((error) => {
          throw new HTTPError(error.response)
        })
    }

    // Retry bad gateway
    case 502: {
      return doRetryFetch(url, { headers, ...options }, delay, attempt)
    }

    // Retry service unavailable
    case 503: {
      return doRetryFetch(url, { headers, ...options }, delay, attempt)
    }

    // Don't retry other errors, just immediately raise
    default: {
      throw new HTTPError(response)
    }
  }
}

/**
 * A retrying version of `fetch` with the given arguments, and returns the response body as JSON.
 *
 * Will retry up to 5 times on certain errors, see `handleError` for particular error handling.
 * @param {String} url The URL to fetch
 * @param {String} method The HTTP method to use
 * @param {Object} headers The HTTP headers to use
 * @param {String} body The body to send
 * @throws {HTTPError} if the response is an HTTP error status
 * @returns {String} The JSON response body
 */
export const doRetryFetch = async (url, { method, headers, body }, delay = 1000, attempt = 0) => {
  // Exponential backoff on retry attempts - 2, 4 then 8 seconds
  if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, Math.pow(2, attempt) * delay))

  const responseJson = await originalFetch(url, { method, headers, body }).then((response) => {
    if (response.ok) return response.json()

    // If the response wasn't ok, handle errors and/or retry
    return handleError(response, url, { method, headers, body }, delay, attempt + 1)
  })

  return responseJson
}

/**
 * Calls `fetch` with the given arguments, and returns the response body as JSON.
 * @param {String} url The URL to fetch
 * @param {String} method The HTTP method to use
 * @param {Object} headers The HTTP headers to use
 * @param {String} body The body to send
 * @throws {HTTPError} if the response is an HTTP error status
 * @returns {String} The JSON response body
 */
const doFetch = async (url, { method, headers, body }) => {
  const response = await fetch(url, {
    method,
    headers,
    body,
    retryOn: [503],
    retryDelay: (attempt) => Math.pow(2, attempt) * 1000, // exponential backoff - 1, 2 then 4 seconds
  })
  if (!response.ok) {
    throw new HTTPError(response)
  } else {
    const responseJson = await response.json()
    return responseJson
  }
}

export default doFetch
